import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import {
  FileText,
  Eye,
  Calendar,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import dayjs from "dayjs";
import { useState, useEffect, useMemo, useRef } from "react";
import { Trans, Plural } from "@lingui/macro";
import API from "@app/services/api";
import { DocumentPreviewModal, SourceDocumentLink } from "./SourceDocument";
import {
  docTypeColor,
  fileFormatLabel,
  sourceDocumentPath,
} from "@app/utils/source-document";
import type { PatientDocument as Document } from "./SourceDocument";

interface EventGroup {
  id_evenement: string;
  event_debut: string | null;
  event_categorie: string | null;
  documents: Document[];
  /** Documents of the whole group, when a page holds only some of them. */
  total: number;
}

/** Documents shown per page. The API still serves the whole list: a passage the reading cites
 *  opens its document wherever it sits in the pages. */
const PAGE_SIZE = 25;

interface PatientDocumentsProps {
  tenantId: string;
  patientId: string;
}

/** Strip HTML tags for plain-text preview snippets. */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function PatientDocuments({
  tenantId,
  patientId,
}: PatientDocumentsProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const [page, setPage] = useState(0);
  const listTop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPage(0);
    API.get({ path: `/patient/${tenantId}/${patientId}/documents` })
      .then((res: { data?: { documents?: Document[] } }) => {
        if (!cancelled) {
          const list = res?.data?.documents;
          setDocuments(Array.isArray(list) ? list : []);
        }
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, patientId]);

  /** Group documents by id_evenement, sorted by event_debut desc.
   *
   * When the event isn't in our local DB (e.g. older than the sync window),
   * `event_debut` and `event_categorie` come back NULL from the backend join.
   * In that case we fall back to the latest document date in the group, so the
   * group still gets a meaningful timeline label and a sortable date.
   */
  const eventGroups = useMemo<EventGroup[]>(() => {
    const map = new Map<string, EventGroup>();
    for (const doc of documents) {
      const key = doc.id_evenement || "__no_event__";
      if (!map.has(key)) {
        map.set(key, {
          id_evenement: key,
          event_debut: doc.event_debut ?? null,
          event_categorie: doc.event_categorie ?? null,
          documents: [],
          total: 0,
        });
      }
      map.get(key)!.documents.push(doc);
    }
    for (const group of map.values()) {
      // Most recent first, undated last — so the first page shows the latest documents.
      group.documents.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
      group.total = group.documents.length;
      // Backfill missing event_debut from the docs' own dates (max date in group).
      if (group.event_debut) continue;
      const maxDocDate = group.documents
        .map((d) => d.date)
        .filter((d): d is string => Boolean(d))
        .sort()
        .at(-1) ?? null;
      group.event_debut = maxDocDate;
    }
    // Sort groups by effective date descending (most recent first); nulls at end.
    return Array.from(map.values()).sort((a, b) => {
      const ta = a.event_debut ? new Date(a.event_debut).getTime() : Number.NEGATIVE_INFINITY;
      const tb = b.event_debut ? new Date(b.event_debut).getTime() : Number.NEGATIVE_INFINITY;
      return tb - ta;
    });
  }, [documents]);

  const pageCount = Math.max(1, Math.ceil(documents.length / PAGE_SIZE));

  /** This page's documents, in the groups' order; a group cut by the page keeps its header. */
  const pageGroups = useMemo<EventGroup[]>(() => {
    const shown = eventGroups
      .flatMap((group) => group.documents.map((doc) => ({ group, doc })))
      .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const byGroup = new Map<string, EventGroup>();
    for (const { group, doc } of shown) {
      if (!byGroup.has(group.id_evenement)) {
        byGroup.set(group.id_evenement, { ...group, documents: [] });
      }
      byGroup.get(group.id_evenement)!.documents.push(doc);
    }
    return Array.from(byGroup.values());
  }, [eventGroups, page]);

  const goToPage = (next: number) => {
    setPage(next);
    listTop.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-slate-500">
              Chargement des documents...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const first = page * PAGE_SIZE + 1;
  const last = Math.min((page + 1) * PAGE_SIZE, documents.length);
  const total = documents.length;
  const current = page + 1;

  return (
    <div ref={listTop} className="scroll-mt-4 space-y-4">
      <Card>
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-center justify-between">
            <CardTitle className="text-primary flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <Trans>Documents non structurés</Trans>
            </CardTitle>
            <Badge variant="outline" className="text-slate-600">
              <Plural
                value={documents.length}
                one="# document"
                other="# documents"
              />
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                <Trans>Aucun document disponible pour ce patient</Trans>
              </p>
              <p className="text-sm text-slate-400 mt-1">
                <Trans>Les documents apparaîtront après la synchronisation avec la source</Trans>
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {pageGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.id_evenement);
                const groupLabel =
                  group.id_evenement === "__no_event__"
                    ? "Documents sans évènement"
                    : group.event_categorie
                      ? `${group.event_categorie}${group.event_debut ? " — " + dayjs(group.event_debut).format("DD MMM YYYY") : ""}`
                      : group.event_debut
                        ? `Évènement du ${dayjs(group.event_debut).format("DD MMM YYYY")}`
                        : `Évènement ${group.id_evenement}`;

                return (
                  <div key={group.id_evenement}>
                    {/* Event group header */}
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors mb-2"
                      onClick={() => toggleGroup(group.id_evenement)}
                    >
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="text-sm font-semibold text-primary">
                          {groupLabel}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-xs text-slate-600 border-slate-300"
                        >
                          <Plural
                            value={group.total}
                            one="# doc"
                            other="# docs"
                          />
                        </Badge>
                      </div>
                      {isCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-slate-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-slate-500" />
                      )}
                    </button>

                    {/* Documents in group */}
                    {!isCollapsed && (
                      <div className="space-y-2 pl-2 border-l-2 border-blue-100 ml-2">
                        {group.documents.map((doc) => {
                          const previewText = doc.has_file
                            ? null
                            : doc.body_html
                              ? stripHtml(doc.body_html)
                              : doc.body ?? null;
                          const format = doc.has_file
                            ? fileFormatLabel(
                                doc.file_mime_type,
                                doc.file_name || doc.document_name,
                              )
                            : null;
                          return (
                            <div
                              key={doc.id_document || doc.id}
                              className="border border-slate-200 rounded-lg p-4 hover:border-primary/50 hover:bg-primary/5 transition-all"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                                    <h3 className="font-medium text-slate-900 truncate">
                                      {doc.document_name ||
                                        doc.file_name ||
                                        "Document"}
                                    </h3>
                                    {format && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs bg-blue-50 text-blue-700"
                                      >
                                        {format}
                                      </Badge>
                                    )}
                                  </div>
                                  {doc.file_name && (
                                    <p className="text-sm text-slate-600 mb-2">
                                      {doc.file_name}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 text-xs text-slate-500">
                                    {doc.date && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {dayjs(doc.date).format("DD/MM/YYYY")}
                                      </span>
                                    )}
                                    {doc.doc_type && (
                                      <Badge
                                        className={docTypeColor(doc.doc_type)}
                                        variant="secondary"
                                      >
                                        {doc.doc_type}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <Button
                                    asChild
                                    variant="outline"
                                    size="sm"
                                    className="text-primary hover:bg-primary/10"
                                  >
                                    <SourceDocumentLink
                                      to={sourceDocumentPath(
                                        tenantId,
                                        patientId,
                                        doc.id,
                                      )}
                                      onOpen={() => setSelectedDocument(doc)}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      <Trans>Voir</Trans>
                                    </SourceDocumentLink>
                                  </Button>
                                </div>
                              </div>
                              {previewText
                                ? (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <p className="text-xs text-slate-600 line-clamp-2">
                                    {previewText}
                                  </p>
                                </div>
                              )
                                : doc.has_file && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <p className="text-xs text-slate-500">
                                    <Trans>
                                      Fichier joint — ouvrir pour afficher
                                    </Trans>
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {pageCount > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                  <p className="text-sm text-slate-500">
                    <Trans>
                      Documents {first} à {last} sur {total}
                    </Trans>
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => goToPage(page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      <Trans>Précédent</Trans>
                    </Button>
                    <span className="text-sm text-slate-600">
                      <Trans>
                        Page {current} sur {pageCount}
                      </Trans>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pageCount - 1}
                      onClick={() => goToPage(page + 1)}
                    >
                      <Trans>Suivant</Trans>
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedDocument && (
        <DocumentPreviewModal
          tenantId={tenantId}
          patientId={patientId}
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}
    </div>
  );
}
